import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store';
import { lobbyWsClient, type LobbyWsClient } from '@/services/lobbyWs';
import type { LobbyEvent, NowPlayingRef, PlaybackState } from '@/types/lobby';
import type { SourceName, StreamRef } from '@/types/streaming';

const DEFAULT_HEARTBEAT_INTERVAL_MS = 4000;
const SUPPRESS_WINDOW_MS = 250;
const PLAY_SEEK_TOLERANCE_S = 0.5;
/**
 * HLS.js gives us frames in the buffer at readyState 4 (HAVE_ENOUGH_DATA);
 * 3 (HAVE_FUTURE_DATA) is the boundary where the next frame is available.
 * Anything below 3 means we don't have the next frame ready — treat that as
 * buffering so the server doesn't compute drift against our stalled position.
 */
const HAVE_FUTURE_DATA = 3;

/**
 * Read the current playback state from a `<video>` element. Mirrors what the
 * server cares about: is this peer actually moving the playhead forward, or
 * should drift calculation ignore them this tick?
 */
function readPlaybackState(video: HTMLVideoElement): PlaybackState {
  if (video.paused) return 'paused';
  if (video.seeking) return 'buffering';
  if (video.readyState < HAVE_FUTURE_DATA) return 'buffering';
  return 'playing';
}

export interface UseLobbyPlayerSyncOptions {
  videoRef: RefObject<HTMLVideoElement>;
  streamRef: StreamRef;
  activeSource: SourceName | null;
  /** Test seam — defaults to the singleton. */
  client?: LobbyWsClient;
  /** Test seam — defaults to 4000ms. */
  intervalMs?: number;
}

export interface UseLobbyPlayerSyncHandle {
  /**
   * Arm the suppress window — for `durationMs` after the call, native
   * play/pause/seeked events on the `<video>` element are NOT forwarded to
   * the lobby. Used by `useSourceFailover.onSwap` so the synthetic events
   * hls.js fires while reloading the manifest don't pull peers around.
   * Defaults to the same short window used for inbound-event bounces.
   */
  suppress(durationMs?: number): void;
  /**
   * True when the hook had to mute the video to satisfy the browser's
   * autoplay policy. Set when a programmatic `video.play()` (cached-snapshot
   * catch-up, inbound `play` / `state` events) rejects without a user
   * gesture and the hook recovers by muting + retrying. Watch surfaces an
   * "Unmute" CTA based on this flag. Auto-clears when the user manually
   * unmutes via the native controls (volumechange listener) or via
   * `unmute()` below.
   */
  autoMuted: boolean;
  /** User action — unmute the video and clear `autoMuted`. */
  unmute(): void;
}

function streamRefToNowPlaying(ref: StreamRef): NowPlayingRef {
  if (ref.type === 'movie') return { kind: 'movie', tmdbId: ref.tmdbId };
  if (ref.type === 'anime') return { kind: 'anime', malId: ref.malId, episode: ref.episode, audio: ref.audio };
  return { kind: 'tv', tmdbId: ref.tmdbId, season: ref.season, episode: ref.episode };
}

function refsEqual(a: NowPlayingRef | null, b: NowPlayingRef | null): boolean {
  if (!a || !b) return a === b;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'movie' && b.kind === 'movie') return a.tmdbId === b.tmdbId;
  if (a.kind === 'tv' && b.kind === 'tv') {
    return a.tmdbId === b.tmdbId && a.season === b.season && a.episode === b.episode;
  }
  if (a.kind === 'anime' && b.kind === 'anime') {
    return a.malId === b.malId && a.episode === b.episode && a.audio === b.audio;
  }
  if (a.kind === 'sports-vod' && b.kind === 'sports-vod') {
    return a.sportsVodId === b.sportsVodId;
  }
  return false;
}

function nowPlayingToPath(ref: NowPlayingRef, source: string | null): string {
  const base = ref.kind === 'movie'
    ? `/watch/movie/${ref.tmdbId}`
    : ref.kind === 'tv'
    ? `/watch/tv/${ref.tmdbId}/${ref.season}/${ref.episode}`
    : ref.kind === 'anime'
    ? `/watch/anime/${ref.malId}/${ref.episode}/${ref.audio}`
    : `/watch/sports-vod/${encodeURIComponent(ref.sportsVodId)}`;
  return source ? `${base}?source=${encodeURIComponent(source)}` : base;
}

export function useLobbyPlayerSync(opts: UseLobbyPlayerSyncOptions): UseLobbyPlayerSyncHandle {
  const {
    videoRef,
    streamRef,
    activeSource,
    client = lobbyWsClient,
    intervalMs = DEFAULT_HEARTBEAT_INTERVAL_MS,
  } = opts;

  const inLobby = useAppSelector((s) => s.lobby.currentLobby != null);
  const myUserId = useAppSelector((s) => s.auth.user?.id ?? null);
  // Cached `state` snapshot from the slice — set when the server's snapshot
  // arrives over the WS. We read it here (rather than subscribing to a fresh
  // 'state' event) so a Watch page that mounts AFTER the snapshot arrived
  // (the common case on a full-page refresh — the WS opens in App.tsx before
  // the player hook subscribes) can still snap to the live group position.
  const playbackSnapshot = useAppSelector((s) => s.lobby.playbackSnapshot);
  const navigate = useNavigate();
  const suppressUntilRef = useRef<number>(0);
  const [autoMuted, setAutoMuted] = useState<boolean>(false);
  /**
   * Wrap `video.play()` so an autoplay rejection (most common cause: the
   * caller is a WS event handler, not a user-gesture handler) falls back
   * to muted playback. Muted autoplay is permitted by Chrome / Safari /
   * Firefox without a gesture, so the joiner sees motion at the live
   * position instead of a frozen frame. The `autoMuted` flag drives an
   * "Unmute" CTA in Watch — one click restores audio. If the muted retry
   * also rejects, the media isn't ready yet; the next incoming play / state
   * event (or a user-initiated play) will retry on its own.
   */
  const tryPlay = useCallback((video: HTMLVideoElement): void => {
    void video.play().catch(() => {
      if (!video.muted) {
        video.muted = true;
        setAutoMuted(true);
      }
      void video.play().catch(() => { /* media not ready or hard block */ });
    });
  }, []);
  /**
   * Snapshots already consumed for the current `<video>` element. We don't
   * want to re-snap every time the user pauses, scrubs, or otherwise produces
   * a `loadedmetadata` / `canplay` re-fire — only on a true cold-mount.
   */
  const appliedSnapshotKeyRef = useRef<string | null>(null);

  // ---------- Heartbeat loop ----------
  useEffect(() => {
    if (!inLobby) return undefined;
    const id = setInterval(() => {
      const video = videoRef.current;
      if (!video) return;
      const ts = Math.floor((Number.isFinite(video.currentTime) ? video.currentTime : 0) * 1000);
      // Send actual playback state so the server can drop paused / buffering
      // peers from drift calculation. Without this, an HLS buffer underrun
      // on one viewer would pull the rest of the lobby backwards every few
      // seconds until the underrun clears.
      client.sendHeartbeat(ts, readPlaybackState(video));
    }, intervalMs);
    return () => clearInterval(id);
  }, [inLobby, client, intervalMs, videoRef]);

  // ---------- Outbound now_playing on mount + when ref/source changes ----------
  // Send immediately when the WS is already open, and subscribe to `onOpen`
  // so the message also goes out the moment a (re)connection is established.
  // Without the onOpen branch, navigating to a new /watch route during a WS
  // reconnect drops the now_playing entirely — the send happens before the
  // socket is OPEN, the client silently swallows it (readyState check), and
  // peers never learn about the new content. Confirmed via Playwright: a
  // full-page navigate that closed + reopened the WS left peers stuck on
  // the prior content.
  useEffect(() => {
    if (!inLobby) return undefined;
    const ref = streamRefToNowPlaying(streamRef);
    client.sendNowPlaying(ref, activeSource);
    const offOpen = client.onOpen(() => {
      client.sendNowPlaying(ref, activeSource);
    });
    return offOpen;
  }, [
    inLobby, client,
    streamRef.type,
    streamRef.type === 'anime' ? streamRef.malId : streamRef.tmdbId,
    streamRef.type === 'tv' ? streamRef.season : -1,
    streamRef.type === 'tv' || streamRef.type === 'anime' ? streamRef.episode : -1,
    streamRef.type === 'anime' ? streamRef.audio : '',
    activeSource,
  ]);

  // ---------- Outbound listener: forward <video> events to server ----------
  useEffect(() => {
    if (!inLobby) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    const isSuppressed = (): boolean => Date.now() < suppressUntilRef.current;
    const ts = (): number => Math.floor((Number.isFinite(video.currentTime) ? video.currentTime : 0) * 1000);

    function onPlay(): void { if (!isSuppressed()) client.sendPlay(ts()); }
    function onPause(): void { if (!isSuppressed()) client.sendPause(ts()); }
    function onSeeked(): void { if (!isSuppressed()) client.sendSeek(ts()); }

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('seeked', onSeeked);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('seeked', onSeeked);
    };
  }, [inLobby, client, videoRef]);

  // ---------- Inbound subscription: apply server events to <video> ----------
  useEffect(() => {
    if (!inLobby) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    function suppress(): void { suppressUntilRef.current = Date.now() + SUPPRESS_WINDOW_MS; }

    function applyTimestamp(playTimestamp: number, tolerant: boolean): void {
      const targetSeconds = playTimestamp / 1000;
      const current = Number.isFinite(video!.currentTime) ? video!.currentTime : 0;
      if (tolerant && Math.abs(current - targetSeconds) < PLAY_SEEK_TOLERANCE_S) return;
      suppress();
      video!.currentTime = targetSeconds;
    }

    const offPlay = client.on('play', (e: LobbyEvent) => {
      if (e.type !== 'play') return;
      if (e.userId === myUserId) return;
      applyTimestamp(e.playTimestamp, /* tolerant */ true);
      suppress();
      // Inbound `play` arrives from a WS handler — no user gesture, so
      // autoplay policy may reject. tryPlay falls back to muted + sets the
      // autoMuted flag, so the joiner sees motion immediately and gets an
      // "Unmute" CTA instead of a frozen frame.
      tryPlay(video!);
    });
    const offPause = client.on('pause', (e: LobbyEvent) => {
      if (e.type !== 'pause') return;
      // Tolerant: the server intentionally broadcasts the laggard's position so
      // the room converges, but forcing a hard seek on every pause (even when
      // we're already within 0.5s of the laggard) produces a visible backward
      // jerk for everyone in tolerance. Real drift > tolerance still seeks.
      applyTimestamp(e.playTimestamp, /* tolerant */ true);
      suppress();
      video!.pause();
    });
    const offSeek = client.on('seek', (e: LobbyEvent) => {
      if (e.type !== 'seek') return;
      if (e.userId === myUserId) return;
      applyTimestamp(e.playTimestamp, /* tolerant */ true);
    });
    const offResync = client.on('resync', (e: LobbyEvent) => {
      if (e.type !== 'resync') return;
      // Tolerant: server-side cooldown means resyncs arrive at most every few
      // seconds, but a sub-tolerance resync (e.g. we've already converged by
      // the time it arrives) shouldn't force a visible seek.
      applyTimestamp(e.playTimestamp, /* tolerant */ true);
    });

    return () => { offPlay(); offPause(); offSeek(); offResync(); };
  }, [inLobby, client, videoRef, myUserId]);

  // ---------- Inbound now_playing → navigate (if it actually differs) ----------
  useEffect(() => {
    if (!inLobby) return undefined;
    const off = client.on('now_playing', (e: LobbyEvent) => {
      if (e.type !== 'now_playing') return;
      if (e.changedByUserId === myUserId) return;
      const current = streamRefToNowPlaying(streamRef);
      if (refsEqual(current, e.ref) && activeSource === e.source) return;
      navigate(nowPlayingToPath(e.ref, e.source));
    });
    return off;
  }, [inLobby, client, myUserId, navigate, streamRef, activeSource]);

  // ---------- Inbound state event: join-mid-playback snap ----------
  useEffect(() => {
    if (!inLobby) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    const off = client.on('state', (e: LobbyEvent) => {
      if (e.type !== 'state') return;
      if (!e.ref) return;
      const currentRef = streamRefToNowPlaying(streamRef);
      if (!refsEqual(currentRef, e.ref)) return;
      // Prefer the server-supplied serverNow to compute the elapsed delta,
      // so a client whose wall-clock disagrees with the server by N seconds
      // doesn't jump N seconds when joining. Fall back to client Date.now()
      // for older backends that don't send the field.
      const elapsedMs = e.lastAnchorAt > 0
        ? (typeof e.serverNow === 'number' ? e.serverNow : Date.now()) - e.lastAnchorAt
        : 0;
      const expectedMs = e.isPlaying ? e.playTimestamp + elapsedMs : e.playTimestamp;
      const expectedSeconds = expectedMs / 1000;
      const current = Number.isFinite(video.currentTime) ? video.currentTime : 0;
      if (Math.abs(current - expectedSeconds) > PLAY_SEEK_TOLERANCE_S) {
        suppressUntilRef.current = Date.now() + SUPPRESS_WINDOW_MS;
        video.currentTime = expectedSeconds;
      }
      if (e.isPlaying) {
        suppressUntilRef.current = Date.now() + SUPPRESS_WINDOW_MS;
        // Same autoplay-policy concern as inbound `play`. tryPlay falls back
        // to muted + sets autoMuted.
        tryPlay(video);
      }
    });
    return off;
  }, [inLobby, client, videoRef, streamRef, tryPlay]);

  // ---------- Cached-snapshot catch-up (refresh-in-lobby) ----------
  // The inbound-'state' subscription above catches the snapshot when it
  // arrives AFTER this hook is mounted. The cached snapshot here covers the
  // refresh case — by the time `Watch` mounts, the App-level WS has already
  // received + dispatched the 'state' event into the slice. We replay it
  // once the <video> has metadata so the snap doesn't get cancelled by HLS
  // metadata loading immediately afterwards.
  useEffect(() => {
    if (!inLobby) return undefined;
    if (!playbackSnapshot) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    const currentRef = streamRefToNowPlaying(streamRef);
    if (!refsEqual(currentRef, playbackSnapshot.ref)) return undefined;

    // Key by (ref, receivedAt) so a brand-new snapshot for the same ref
    // (e.g. WS reconnect after a network blip) still re-syncs the player.
    const snapKey = JSON.stringify({ r: playbackSnapshot.ref, t: playbackSnapshot.receivedAt });
    if (appliedSnapshotKeyRef.current === snapKey) return undefined;

    function applySnapshot(): void {
      if (!video || !playbackSnapshot) return;
      // We computed expectedMs at apply-time (not at snapshot-receipt) so the
      // playhead lands on where the group is NOW, not where they were when
      // the WS first opened. For a refresh that included an HLS warmup, the
      // delta between snapshot arrival and metadata-load is exactly the
      // catch-up we need.
      const elapsedSinceAnchor = playbackSnapshot.isPlaying
        ? Math.max(0, (Date.now() - playbackSnapshot.receivedAt) + (playbackSnapshot.serverNow - playbackSnapshot.lastAnchorAt))
        : 0;
      const expectedMs = playbackSnapshot.isPlaying
        ? playbackSnapshot.playTimestamp + elapsedSinceAnchor
        : playbackSnapshot.playTimestamp;
      const expectedSeconds = expectedMs / 1000;
      const current = Number.isFinite(video.currentTime) ? video.currentTime : 0;
      if (Math.abs(current - expectedSeconds) > PLAY_SEEK_TOLERANCE_S) {
        suppressUntilRef.current = Date.now() + SUPPRESS_WINDOW_MS;
        video.currentTime = expectedSeconds;
      }
      if (playbackSnapshot.isPlaying) {
        suppressUntilRef.current = Date.now() + SUPPRESS_WINDOW_MS;
        // Cached-snapshot catch-up runs on Watch mount immediately after the
        // user clicked through a Join Prompt. The click happened on a
        // DIFFERENT element (the prompt button) than the just-mounted
        // <video>, so it doesn't satisfy the autoplay policy for THIS
        // element. tryPlay falls back to muted, so the joiner sees the
        // live position playing back rather than a frozen frame at t=0.
        tryPlay(video);
      }
      appliedSnapshotKeyRef.current = snapKey;
    }

    // If metadata is already loaded (e.g. the snapshot arrived later than the
    // HLS bootstrap), apply immediately. Otherwise wait for it.
    if (video.readyState >= 1 /* HAVE_METADATA */) {
      applySnapshot();
      return undefined;
    }
    video.addEventListener('loadedmetadata', applySnapshot, { once: true });
    return () => { video.removeEventListener('loadedmetadata', applySnapshot); };
  }, [inLobby, playbackSnapshot, streamRef, videoRef]);

  // Reset the applied-key when the user navigates to a different stream so a
  // future snapshot for the new ref gets applied.
  useEffect(() => {
    appliedSnapshotKeyRef.current = null;
  }, [
    streamRef.type,
    streamRef.type === 'anime' ? streamRef.malId : streamRef.tmdbId,
    streamRef.type === 'tv' ? streamRef.season : -1,
    streamRef.type === 'tv' || streamRef.type === 'anime' ? streamRef.episode : -1,
    streamRef.type === 'anime' ? streamRef.audio : '',
  ]);

  // Clear `autoMuted` if the user manually unmutes via the native <video
  // controls> volume slider — keeps the CTA from sticking around after the
  // user already restored audio.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    function onVolumeChange(): void {
      if (video && !video.muted) setAutoMuted(false);
    }
    video.addEventListener('volumechange', onVolumeChange);
    return () => { video.removeEventListener('volumechange', onVolumeChange); };
  }, [videoRef]);

  const unmute = useCallback((): void => {
    const v = videoRef.current;
    if (v) v.muted = false;
    setAutoMuted(false);
  }, [videoRef]);

  return useMemo<UseLobbyPlayerSyncHandle>(() => ({
    suppress(durationMs = SUPPRESS_WINDOW_MS): void {
      suppressUntilRef.current = Date.now() + durationMs;
    },
    autoMuted,
    unmute,
  }), [autoMuted, unmute]);
}
